import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { useI18n } from "../i18n";
import { logError } from "../lib/log";
import * as apiClient from "../api";

export default function Concierge() {
  const { t } = useI18n();
  const [message, setMessage] = useState("");
  const [people, setPeople] = useState(2);
  const [days, setDays] = useState(3);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [fallback, setFallback] = useState(false);

  const ask = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);
    setPlan(null);
    try {
      const res = await apiClient.askConcierge({ message, people, days });
      setPlan(res.plan);
      setFallback(!!res.fallback);
    } catch (err) {
      logError("concierge", err);
      setPlan(null);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="bg-gradient-to-br from-primary to-[#003A69] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2 flex items-center gap-2"><Sparkles className="h-4 w-4" /> AI Concierge</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight">{t("concierge.title")}</h1>
          <p className="text-white/70 mt-2">{t("concierge.sub")}</p>
          <form onSubmit={ask} className="mt-6 glass rounded-2xl p-4 space-y-3">
            <textarea data-testid="concierge-input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("concierge.placeholder")} className="w-full rounded-xl bg-white/90 px-4 py-3 text-sm text-slate-800 outline-none resize-none" />
            <div className="flex flex-wrap gap-3 items-center">
              <label className="flex items-center gap-2 rounded-xl bg-white/90 px-4 py-2.5 text-sm text-slate-800">Persone
                <input type="number" min="1" value={people} onChange={(e) => setPeople(Number(e.target.value))} className="w-14 bg-transparent outline-none font-semibold" />
              </label>
              <label className="flex items-center gap-2 rounded-xl bg-white/90 px-4 py-2.5 text-sm text-slate-800">Giorni
                <input type="number" min="1" max="10" value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-14 bg-transparent outline-none font-semibold" />
              </label>
              <button data-testid="concierge-ask-btn" disabled={loading} className="ml-auto flex items-center gap-2 rounded-full bg-white text-primary font-semibold px-6 py-2.5 disabled:opacity-60">
                <Send className="h-4 w-4" /> {loading ? t("common.loading") : t("concierge.ask")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {loading && (
          <div className="text-center py-16">
            <Sparkles className="h-10 w-10 text-secondary mx-auto animate-pulse" />
            <p className="text-slate-500 mt-3">Cristalda sta creando il tuo itinerario...</p>
          </div>
        )}
        {plan && (
          <div className="animate-float-up" data-testid="concierge-result">
            {fallback && <div className="mb-4 rounded-xl bg-amber-50 text-amber-700 text-sm px-4 py-2">Suggerimento generato in modalità offline.</div>}
            <p className="text-lg text-slate-700 mb-6">{plan.intro}</p>
            <div className="space-y-6">
              {plan.days?.map((d) => (
                <div key={d.day} className="rounded-2xl border border-slate-100 p-6 bg-white shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="h-9 w-9 rounded-full bg-primary text-white font-bold flex items-center justify-center">{d.day}</span>
                    <h3 className="font-heading font-semibold text-lg text-slate-900">{d.title}</h3>
                  </div>
                  <div className="space-y-3">
                    {d.experiences?.map((ex, i) => (
                      <Link key={ex.slug || `exp-${d.day}-${i}`} to={ex.slug ? `/esperienza/${ex.slug}` : "/esperienze"} className="flex items-start gap-3 rounded-xl border border-slate-100 p-3 hover:border-primary transition-colors">
                        <MapPin className="h-4 w-4 text-secondary mt-1 shrink-0" />
                        <div>
                          <div className="font-semibold text-slate-800">{ex.title}</div>
                          <div className="text-sm text-slate-500">{ex.why}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {plan.closing && <p className="mt-6 font-accent italic text-primary text-xl">{plan.closing}</p>}
          </div>
        )}
      </div>
    </Layout>
  );
}
